import { NetworkStatus, useQuery } from '@apollo/client';
import { IconButton, PrimaryButton, Stack, Text } from '@fluentui/react';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { UserGroupFilter } from 'common/types/globalTypes';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getColumns } from './column.data';
import { ColumnStylesProps, onRenderItems } from './columns.render';
import { useStyles } from './index.styles';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';
import {
  UserGroups,
  UserGroups_userGroups_nodes,
  UserGroupsVariables,
} from './__generated__/UserGroups';
import { filterOptionProps, ListFilters } from './Filters';
import USER_GROUPS from './UserGroups.graphql';
import { onHandleSearch, toFilterVariable } from './utils';
import { UserGroupModal } from '../view/UserGroupModal';
import { DeleteUserGroup } from './DeleteUserGroup';

export type UserGroupRowItem = UserGroups_userGroups_nodes;

export const UserGroupsList: React.FC = () => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [filterOptions, setFilterOptions] = useState<filterOptionProps>({
    filterTypes: [],
    startsWith: false,
  });
  const renderRef = useRef(false);
  const columnStyles: ColumnStylesProps = {
    onRenderColumnStack: styles.onRenderColumnStack,
  };
  const [selectedRows, setSelectedRows] = useState<UserGroupRowItem[]>([]);
  const {
    data: userGroupsData,
    loading: userGroupsLoading,
    variables,
    networkStatus,
    refetch,
    fetchMore,
  } = useQuery<UserGroups, UserGroupsVariables>(USER_GROUPS, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['NAME_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });
  const refetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;

  const { nodes, pageInfo } = {
    ...userGroupsData?.userGroups,
  };
  const { hasNextPage } = { ...pageInfo };
  const transformedData: UserGroupRowItem[] = refetching
    ? []
    : nodes?.map((item) => ({
        ...item,
      })) || [];

  const handleSearch = async (showMore: boolean) => {
    const { queryVariables } = onHandleSearch(
      showMore,
      sortOrderParam,
      userGroupsData
    );
    if (showMore) fetchMore?.({ variables: queryVariables });
    else refetch(queryVariables);
  };

  const handleSearchMemo = useCallback(handleSearch, [sortOrderParam]);

  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };
  const _renderItemColumn = useCallback(onRenderItems(columnStyles), [
    columnStyles,
  ]);

  const setCustomColumns = useCallback(() => {
    setGridColumns(getColumns());
  }, []);
  const onRefresh = () => {
    refetch();
  };
  const onFilterChange = (filterOptions: filterOptionProps) => {
    setFilterOptions(filterOptions);
    const filterArray: UserGroupFilter | undefined = filterOptions.filterTypes
      ?.length
      ? ({ and: toFilterVariable(filterOptions) } as UserGroupFilter)
      : undefined;
    refetch({ ...variables, filter: filterArray! });
  };
  useEffect(() => {
    setCustomColumns();
  }, [setCustomColumns]);

  useEffect(() => {
    if (renderRef.current) {
      handleSearchMemo(false);
    } else renderRef.current = true;
  }, [handleSearchMemo]);

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">User Groups</Text>
            <HelpIcon appLinkType="USER_GROUPS" />
          </Stack>
          <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 10 }}>
            <ListFilters
              filterOptions={filterOptions}
              onFilterChange={onFilterChange}
            />
            <IconButton
              iconProps={{ iconName: 'refresh' }}
              onClick={onRefresh}
            />
            <DeleteUserGroup selectedRows={selectedRows} />
            <PrimaryButton
              iconProps={{
                iconName: 'Add',
              }}
              text="New User Group"
              onClick={() => setIsOpen(true)}
            />
          </Stack>
        </Stack>
      </Stack>
      <InfiniteList
        loading={userGroupsLoading}
        items={transformedData}
        hasNextPage={hasNextPage}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onRenderItemColumn={_renderItemColumn}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onSelectionChanged={setSelectedRows}
        onLoadMore={async () => {
          await handleSearch(true);
        }}
      />

      {isOpen && <UserGroupModal onClose={() => setIsOpen(false)} />}
    </>
  );
};
